import { useState, useRef, useEffect } from 'react';

/**
 * useSharedResult — the output panel's "a result opened from chat" state, and
 * the one rule for when it stands down.
 *
 * A snapshot opened from a chat card (a share link, or a finished run) TAKES
 * OVER the panel. The live `events` are untouched and keep arriving underneath,
 * so this is a state flip, not a refetch, and a split second screen or a Presto
 * watching the same station never sees it.
 *
 * **A new live output event clears it.** That is the whole reason this is a
 * hook and not a `useState` at the call site.
 *
 * `run-result-unfurl` removed the "Back to live output" button — for a run of
 * THIS station the result IS the station's output, so a control offering to go
 * "back" to it explained nothing. But that reasoning holds only for the moment
 * of opening. Ask the station anything else afterwards and its cards land in a
 * panel still showing the old snapshot, with nothing to dismiss it: the panel
 * is frozen until a reload (!747 review). So the arrival of newer output IS the
 * dismissal — the result was worth looking at until the station produced
 * something newer, and then the newer thing wins. No button, no freeze.
 *
 * Keyed on the ARRAY IDENTITY of `events`, which is what the reducer replaces
 * when output changes — the same signal the drawer-open effect in
 * ChatInterface already trusts. NOT on length: a card that replaces the panel's
 * contents without adding to it is still newer output.
 *
 * `prev` starts AT `events` so the mount pass is a no-op rather than a clear.
 * Defensive only, and deliberately NOT claimed as a guard: `shared` starts
 * null, so initialising `prev` to null instead would clear nothing and behave
 * identically. A test for it passes against its own mutation, which is why
 * there isn't one.
 */
export function useSharedResult(events) {
  const [shared, setShared] = useState(null);
  const prev = useRef(events);
  useEffect(() => {
    if (events !== prev.current) {
      prev.current = events;
      // Unconditional: `setShared(null)` on an already-null state is a no-op in
      // React, so there is nothing to guard and a guard would only be a second
      // place for this rule to be written.
      setShared(null);
    }
  }, [events]);
  return [shared, setShared];
}

// The panel state for a shared card, from its `/api/embed/s/<id>` body (null on
// failure). `bookmarked` is null unless the server offered the bookmark.
// A chat session's own card also says when it was made (`asOf`) and whether it may be
// refreshed (autoroute-continuity-pin).
export function sharedResultFrom(preview, body) {
  const cards = body && Array.isArray(body.cards) ? body.cards : [];
  const bookmarked = body && typeof body.bookmarked === 'boolean' ? body.bookmarked : null;
  return { id: preview.id, title: preview.title, cards, bookmarked, loading: false,
           asOf: (body && body.as_of) || null, refreshable: !!(body && body.refreshable) };
}

// "as of 10:02" for a card made today, with the date otherwise.
export function asOfLabel(iso, now = new Date()) {
  const at = iso ? new Date(iso) : null;
  if (!at || Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return at.toDateString() === now.toDateString()
    ? `as of ${time}` : `as of ${at.toLocaleDateString()} ${time}`;
}

// Embed and Save act on the station's own output. While a shared card covers it,
// they'd read as acting on the shared card, so they're hidden.
export function stationOutputActions({ embedPublic, savable, shared }) {
  return { embed: !!embedPublic && !shared, save: !!savable && !shared };
}
